import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Users, UserSquare2, CalendarDays, Inbox, Wrench, FolderKanban,
  FileText, Receipt, DollarSign, MessageSquare, BookOpen, Bot, Newspaper, Settings,
  ShieldCheck, Bell, Search, ChevronLeft, LogOut, Menu, Cog, Tags, Repeat, FileBarChart, PenSquare, Image, Mails, Megaphone, CreditCard, Contact, FileSignature
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/brand/Logo";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["owner","admin","technician","sales","content_manager"] },
  { to: "/dashboard/leads", label: "Leads", icon: Users, roles: ["owner","admin","sales"] },
  { to: "/dashboard/clients", label: "Clients", icon: Contact, roles: ["owner","admin","sales"] },
  { to: "/dashboard/contracts", label: "Contracts", icon: FileSignature, roles: ["owner","admin"] },
  { to: "/dashboard/requests", label: "Service Requests", icon: Inbox, roles: ["owner","admin","technician","sales","content_manager"] },
  { to: "/dashboard/appointments", label: "Appointments", icon: CalendarDays, roles: ["owner","admin","technician","sales","content_manager"] },
  { to: "/dashboard/repair-jobs", label: "Repair Jobs", icon: Wrench, roles: ["owner","admin","technician","sales"] },
  { to: "/dashboard/projects", label: "Projects", icon: FolderKanban, roles: ["owner","admin","sales"] },
  { to: "/dashboard/estimates", label: "Estimates", icon: FileText, roles: ["owner","admin","sales"] },
  { to: "/dashboard/invoices", label: "Invoices", icon: Receipt, roles: ["owner","admin","sales"] },
  { to: "/dashboard/recurring", label: "Recurring Billing", icon: Repeat, roles: ["owner","admin","sales"] },
  { to: "/dashboard/statements", label: "Statements", icon: FileBarChart, roles: ["owner","admin","sales"] },
  { to: "/dashboard/downloads", label: "Digital Downloads", icon: FolderKanban, roles: ["owner","admin","content_manager"] },
  { to: "/dashboard/reports", label: "Reports", icon: LayoutDashboard, roles: ["owner","admin","sales"] },
  { to: "/dashboard/services", label: "Services", icon: Wrench, roles: ["owner","admin"] },
  { to: "/dashboard/pricing", label: "Pricing", icon: Tags, roles: ["owner","admin"] },
  { to: "/dashboard/knowledge", label: "Knowledge Base", icon: BookOpen, roles: ["owner","admin","content_manager"] },
  { to: "/dashboard/ai", label: "AI Conversations", icon: Bot, roles: ["owner","admin"] },
  { to: "/dashboard/content", label: "Website Content", icon: Newspaper, roles: ["owner","admin","content_manager"] },
  { to: "/dashboard/blog", label: "Blog", icon: PenSquare, roles: ["owner","admin","content_manager"] },
  { to: "/dashboard/portfolio", label: "Portfolio", icon: Image, roles: ["owner","admin","content_manager"] },
  { to: "/dashboard/email-templates", label: "Email Templates", icon: Mails, roles: ["owner","admin"] },
  { to: "/dashboard/broadcasts", label: "Broadcasts", icon: Megaphone, roles: ["owner","admin"] },
  { to: "/dashboard/users", label: "Users & Roles", icon: UserSquare2, roles: ["owner","admin"] },
  { to: "/dashboard/subscription", label: "Subscription", icon: CreditCard, roles: ["owner","admin"] },
  { to: "/dashboard/audit", label: "Audit Logs", icon: ShieldCheck, roles: ["owner","admin"] },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const { data: notifs, refetch } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then(r => r.data), refetchInterval: 30000 });

  const links = NAV.filter(n => !n.roles || n.roles.includes(user?.role));
  const doLogout = async () => { await logout(); nav("/owner/login"); };
  const markRead = async () => { await api.put("/notifications/read"); refetch(); };
  const clearNotifs = async () => { try { await api.delete("/notifications"); refetch(); } catch (e) {} };
  const canClear = ["owner", "admin"].includes(user?.role);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={cn("bg-navy text-slate-300 flex flex-col transition-all duration-200 fixed lg:sticky top-0 h-screen z-40",
        collapsed ? "w-16" : "w-64", mobileOpen ? "left-0" : "-left-64 lg:left-0")}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          {!collapsed && <Logo variant="wordmark-light" />}
          <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:block p-1.5 rounded hover:bg-white/10" data-testid="sidebar-collapse">
            <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {links.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
              data-testid={`sidebar-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => cn("flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                isActive ? "bg-electric text-white" : "hover:bg-white/10")}>
              <n.icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
              {!collapsed && <span>{n.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-white/10 w-full">
            <LogOut className="h-4 w-4" /> {!collapsed && "Sign out"}
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setMobileOpen(true)} data-testid="mobile-sidebar-toggle"><Menu className="h-5 w-5" /></button>
            <div className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-100 text-slate-400 text-sm w-64">
              <Search className="h-4 w-4" /> <span>Search…</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) markRead(); }} className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="notifications-btn">
                <Bell className="h-5 w-5 text-slate-600" />
                {notifs?.unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-electric text-white text-[9px] grid place-items-center">{notifs.unread}</span>}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 max-h-96 overflow-y-auto z-50" data-testid="notifications-panel">
                  <div className="p-3 border-b flex items-center justify-between">
                    <span className="font-heading font-semibold text-navy text-sm">Notifications</span>
                    {canClear && (notifs?.notifications || []).length > 0 &&
                      <button onClick={clearNotifs} className="text-xs text-slate-400 hover:text-red-600 transition-colors" data-testid="notifications-clear-btn">Clear all</button>}
                  </div>
                  {(notifs?.notifications || []).length === 0 ? <p className="p-4 text-sm text-slate-500">No notifications yet.</p> :
                    (notifs?.notifications || []).map((n) => (
                      <div key={n.id} className="p-3 border-b border-slate-100 hover:bg-slate-50">
                        <p className="text-sm font-medium text-navy">{n.title}</p>
                        <p className="text-xs text-slate-500">{n.body}</p>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-electric to-tech grid place-items-center text-white text-xs font-semibold">{user?.name?.[0] || "U"}</div>
              <div className="hidden sm:block leading-tight">
                <p className="text-sm font-medium text-navy">{user?.name}</p>
                <p className="text-[11px] text-metallic capitalize font-mono">{user?.role?.replace("_", " ")}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function PageTitle({ title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-navy">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
